import React, { useEffect, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Select, InputLabel, FormControl, Alert, Stack, Chip, IconButton, Tooltip, Box, Tabs, Tab } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import api, { baseURL } from '../services/api';
import { fetchCurrentUser } from '../services/auth';
import { requestLeave, myLeaves, pendingLeaves, reviewQueueLeaves, reviewLeave, observeLeave, allLeaves } from '../services/leaves';
import { requestAbsence, requestAbsenceWithAttachment, getMyAbsences, getAllAbsences, updateAbsenceStatus, deleteAbsence } from '../services/absences';
import PDFModalLeave from '../components/PDFModalLeave';
import LeavePDFViewer from '../components/LeavePDFViewer';

const LeavesPage = () => {
  const [me, setMe] = useState(null);
  const [allRows, setAllRows] = useState([]);
  const [absenceRows, setAbsenceRows] = useState([]);
  const [currentTab, setCurrentTab] = useState(0); // 0: Congés, 1: Absences
  const [open, setOpen] = useState(false);
  const [absenceOpen, setAbsenceOpen] = useState(false);
  const [form, setForm] = useState({ employee_id: '', start_date: '', end_date: '', reason: '', type: 'annuel', address_during_leave: '', phone_number: '', interim_id: '', attachment: null });
  const [absenceForm, setAbsenceForm] = useState({ employee_id: '', start_date: '', end_date: '', reason: '', type: 'maladie', address_during_leave: '', phone_number: '', interim_id: '', attachment: null });
  const [allEmployees, setAllEmployees] = useState([]);
  const [interimEmployees, setInterimEmployees] = useState([]);
  const roleId = Number(me?.role_id ?? me?.role);
  const isAdmin = roleId === 1;
  const isAdminTenes = roleId === 5;
  const isComptable = roleId === 3;
  const canSeeAll = isAdmin || isAdminTenes || isComptable; // all list visible; backend filters for comptable
  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfType, setPdfType] = useState('request'); // 'request' | 'accepted'
  const [pdfData, setPdfData] = useState(null);
  // Review dialog (observations on approve/reject)
  const [revOpen, setRevOpen] = useState(false);
  const [revLeaveId, setRevLeaveId] = useState(null);
  const [revAction, setRevAction] = useState('approve');
  const [revType, setRevType] = useState('leave'); // 'leave' ou 'absence'
  const [revObsDir, setRevObsDir] = useState('');
  const [revObsResp, setRevObsResp] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [editingLeave, setEditingLeave] = useState(null);
  const [editingType, setEditingType] = useState('leave');
  const [editForm, setEditForm] = useState({
    start_date: '',
    end_date: '',
    reason: '',
    type: 'annuel',
    address_during_leave: '',
    phone_number: '',
    interim_id: ''
  });
  const [revSubmitting, setRevSubmitting] = useState(false);
  // Observation-only dialog (for review queue)
  const [obsOpen, setObsOpen] = useState(false);
  const [obsLeaveId, setObsLeaveId] = useState(null);
  const [obsRole, setObsRole] = useState(null); // 'responsable' | 'directeur'
  const [obsText, setObsText] = useState('');
  const [obsSubmitting, setObsSubmitting] = useState(false);
  const [leaveSubmitting, setLeaveSubmitting] = useState(false);
  const [absenceSubmitting, setAbsenceSubmitting] = useState(false);
  // Details modal state
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedDetails, setSelectedDetails] = useState(null);
  const [detailsType, setDetailsType] = useState('leave'); // 'leave' or 'absence'
  // Filters
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');

  // Type mapping for display
  const getTypeLabel = (type) => {
    const typeMap = {
      // Types de congés
      'annuel': 'Annuel',
      'sans_solde': 'Sans Solde',
      'autre': 'Autre',
      // Types d'absences
      'maladie': 'Maladie',
      'maternite': 'Maternité',
      'recuperation': 'Récupération',
      'accident_travail': 'Accident de travail',
      'mariage': 'Evenement familial : Mariage',
      'deces_famille': 'Evenement familial : Décès',
      'formation': 'Formation',
      'evenement_familial_naissance': 'Evenement familial : Naissance',
      'evenement_familial_circoncision': 'Evenement familial : Circoncision (ختان)',
      'autre': 'Autre'
    };
    return typeMap[type] || type || '—';
  };

  // Helper function to calculate days difference
  const calculateDays = (startDate, endDate) => {
    if (!startDate || !endDate) return '—';
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return '—';
    
    // Calculate difference in days (inclusive)
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    
    return diffDays;
  };
  const [filterEmployee, setFilterEmployee] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const getStatusChip = (leave) => {
    if (leave.status === 'rejected' || leave.refused) {
      return <Chip label="Refusé" color="error" size="small" />;
    } else if (leave.status === 'approved' || leave.approved) {
      return <Chip label="Validé" color="success" size="small" />;
    } else {
      return <Chip label="En attente" color="warning" size="small" />;
    }
  };

  const filterOptions = [
    { value: 'all', label: 'Toutes les demandes' },
    { value: 'approved', label: 'Approuvées' },
    { value: 'rejected', label: 'Refusées' },
    { value: 'pending', label: 'En attente' }
  ];

  const load = async () => {
    try {
      if (canSeeAll) {
        const all = await allLeaves();
        
        // FIX: Handle nested data structure
        const leavesData = all.data?.data || all.data || [];
        
        const rows = (Array.isArray(leavesData) ? leavesData : []).map((r) => {
          const emp = r.employee || r.Employee || {};
          const name = `${emp.first_name || ''} ${emp.last_name || ''}`.trim();
          return { ...r, employeeName: name || '—' };
        });
        setAllRows(rows);
      } else {
        // For non-admin users, show only their own leaves
        const mine = await myLeaves();
        
        // FIX: Handle nested data structure
        const leavesData = mine.data?.data || mine.data || [];
        
        const rows = (Array.isArray(leavesData) ? leavesData : []).map((r) => {
          const emp = r.employee || r.Employee || {};
          const name = `${emp.first_name || ''} ${emp.last_name || ''}`.trim();
          return { ...r, employeeName: name || '—' };
        });
        setAllRows(rows);
      }
    } catch (error) {
      setAllRows([]);
    }
  };

  const loadAbsences = async () => {
    try {
      if (canSeeAll) {
        const all = await getAllAbsences();
        
        // FIX: Handle nested data structure
        const absencesData = all.data?.data || all.data || [];
        
        const rows = (Array.isArray(absencesData) ? absencesData : []).map((r) => {
          const emp = r.employee || r.Employee || {};
          const name = `${emp.first_name || ''} ${emp.last_name || ''}`.trim();
          return { ...r, employeeName: name || '—' };
        });
        setAbsenceRows(rows);
      } else {
        // For non-admin users, show only their own absences
        const mine = await getMyAbsences(me?.employee?.id);
        
        // FIX: Handle nested data structure
        const absencesData = mine.data?.data || mine.data || [];
        
        const rows = (Array.isArray(absencesData) ? absencesData : []).map((r) => {
          const emp = r.employee || r.Employee || {};
          const name = `${emp.first_name || ''} ${emp.last_name || ''}`.trim();
          return { ...r, employeeName: name || '—' };
        });
        setAbsenceRows(rows);
      }
    } catch (error) {
      setAbsenceRows([]);
    }
  };

  // Apply filters to the rows
  const filteredRows = allRows.filter(row => {
    const statusMatch = filterStatus === 'all' || 
      (filterStatus === 'approved' && (row.status === 'approved' || row.approved)) ||
      (filterStatus === 'rejected' && (row.status === 'rejected' || row.refused)) ||
      (filterStatus === 'pending' && !(row.status === 'approved' || row.approved) && !(row.status === 'rejected' || row.refused));
    
    const typeMatch = filterType === 'all' || row.type === filterType;
    
    const employeeMatch = !filterEmployee || 
      row.employeeName.toLowerCase().includes(filterEmployee.toLowerCase());
    
    const dateMatch = (!dateFrom && !dateTo) || 
      (dateFrom && new Date(row.start_date) >= new Date(dateFrom)) &&
      (dateTo && new Date(row.end_date) <= new Date(dateTo));
    
    return statusMatch && typeMatch && employeeMatch && dateMatch;
  });

  // Apply filters to absence rows
  const filteredAbsenceRows = absenceRows.filter(row => {
    const statusMatch = filterStatus === 'all' || 
      (filterStatus === 'approved' && (row.status === 'approved' || row.approved)) ||
      (filterStatus === 'rejected' && (row.status === 'rejected' || row.refused)) ||
      (filterStatus === 'pending' && !(row.status === 'approved' || row.approved) && !(row.status === 'rejected' || row.refused));
    
    const typeMatch = filterType === 'all' || row.type === filterType;
    
    const employeeMatch = !filterEmployee || 
      row.employeeName.toLowerCase().includes(filterEmployee.toLowerCase());
    
    const dateMatch = (!dateFrom && !dateTo) || 
      (dateFrom && new Date(row.start_date) >= new Date(dateFrom)) &&
      (dateTo && new Date(row.end_date) <= new Date(dateTo));
    
    return statusMatch && typeMatch && employeeMatch && dateMatch;
  });

  useEffect(() => {
    (async () => {
      const user = await fetchCurrentUser();
      setMe(user);
    })();
  }, []);

  // Load employees for interim selection
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        if (isAdmin) {
          // ADMIN: afficher tous les employés
          const res = await api.get('/employees');
          setAllEmployees(res.data || []);
        } else if (isAdminTenes) {
          // ADMIN-TENES: afficher les employés du site TENES uniquement (déjà filtré par le backend)
          const res = await api.get('/employees');
          setAllEmployees(res.data || []);
        } else {
          // AUTRES: afficher seulement le user connecté
          if (me?.employee) {
            setAllEmployees([me.employee]);
          }
        }
      } catch (e) {
        // silent fail
      }
    };
    fetchEmployees();
  }, [isAdmin, isAdminTenes, me?.employee]);

  // Load all employees for interim selection (independent of user role)
  useEffect(() => {
    const fetchInterimEmployees = async () => {
      try {
        const res = await api.get('/employees');
        setInterimEmployees(res.data || []);
      } catch (e) {
        // silent fail
      }
    };
    fetchInterimEmployees();
  }, []);

  useEffect(() => {
    if (me) {
      load();
      loadAbsences();
    }
    // eslint-disable-next-line
  }, [me]);

  const handleRowClick = async (params, type = 'leave') => {
    try {
      let response;
      
      if (type === 'absence') {
        response = await api.get(`/absences/${params.row.id}`);
        
        // S'assurer que les données sont correctement structurées
        let absenceData = response.data;
        
        // Si l'employé n'est pas inclus mais qu'on a employee_id, utiliser les données de la ligne
        if (!absenceData.employee && params.row.employee) {
          absenceData.employee = params.row.employee;
        }
        
        // Si on n'a pas l'employé ni dans la réponse ni dans la ligne, mais qu'on a employeeName
        if (!absenceData.employee && params.row.employeeName && absenceData.employee_id) {
          // Garder employeeName comme fallback
          absenceData.employeeName = params.row.employeeName;
        }
        
        setDetailsType(type);
        setSelectedDetails(absenceData);
        setDetailsOpen(true);
      } else {
        response = await api.get(`/leaves/${params.row.id}`);
        
        // S'assurer que les données sont correctement structurées
        let leaveData = response.data;
        
        // Si l'employé n'est pas inclus mais qu'on a employee_id, utiliser les données de la ligne
        if (!leaveData.employee && params.row.employee) {
          leaveData.employee = params.row.employee;
        }
        
        // Si on n'a pas l'employé ni dans la réponse ni dans la ligne, mais qu'on a employeeName
        if (!leaveData.employee && params.row.employeeName && leaveData.employee_id) {
          // Garder employeeName comme fallback
          leaveData.employeeName = params.row.employeeName;
        }
        
        setDetailsType(type);
        setSelectedDetails(leaveData);
        setDetailsOpen(true);
      }
      
    } catch (error) {
    }
  };

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const onEditChange = (e) => setEditForm({ ...editForm, [e.target.name]: e.target.value });
  const onOpen = () => setOpen(true);
  const onClose = () => setOpen(false);
  const onAbsenceOpen = () => setAbsenceOpen(true);
  const onAbsenceClose = () => setAbsenceOpen(false);
  const onAbsenceChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'attachment' && files && files[0]) {
      setAbsenceForm({ ...absenceForm, [name]: files[0] });
    } else {
      setAbsenceForm({ ...absenceForm, [name]: value });
    }
  };
  const handleTabChange = (event, newValue) => setCurrentTab(newValue);
  const onEditOpen = (leave, type = 'leave') => {
    setEditingType(type);
    setEditingLeave(leave);
    setEditForm({
      start_date: leave.start_date ? new Date(leave.start_date).toISOString().split('T')[0] : '',
      end_date: leave.end_date ? new Date(leave.end_date).toISOString().split('T')[0] : '',
      reason: leave.reason || '',
      type: leave.type || 'annuel',
      address_during_leave: leave.address_during_leave || '',
      phone_number: leave.phone_number || '',
      interim_id: leave.interim_id || ''
    });
    setEditOpen(true);
  };
  const onEditClose = () => {
    setEditOpen(false);
    setEditingLeave(null);
    setEditingType('leave');
    setEditForm({
      start_date: '',
      end_date: '',
      reason: '',
      type: 'annuel',
      address_during_leave: '',
      phone_number: '',
      interim_id: ''
    });
  };
  const openObsDialog = (id, role) => {
    setObsLeaveId(id);
    setObsRole(role);
    setObsText('');
    setObsOpen(true);
  };

  const onSubmit = async () => {
    if (leaveSubmitting) {
      return; // Prevent multiple submissions
    }
    
    const empId = (isAdmin || isAdminTenes)
      ? Number(form.employee_id)
      : me?.employee?.id;
    if (!empId) {
      alert("Veuillez sélectionner un employé");
      return;
    }
    
    // Validate required fields
    if (!form.start_date || !form.end_date) {
      alert("Veuillez sélectionner les dates de début et de fin");
      setLeaveSubmitting(false);
      return;
    }
    
    if (!form.reason || form.reason.trim() === '') {
      alert("Veuillez fournir un motif pour le congé");
      setLeaveSubmitting(false);
      return;
    }
    
    if (new Date(form.start_date) > new Date(form.end_date)) {
      alert("La date de début ne peut pas être après la date de fin");
      setLeaveSubmitting(false);
      return;
    }
    
    setLeaveSubmitting(true);
    
    try {
      
      if (form.attachment) {
        // Use FormData for file upload
        const formData = new FormData();
        formData.append('start_date', form.start_date);
        formData.append('end_date', form.end_date);
        formData.append('reason', form.reason);
        formData.append('type', form.type);
        formData.append('address_during_leave', form.address_during_leave);
        formData.append('phone_number', form.phone_number);
        if (form.interim_id) {
          formData.append('interim_id', form.interim_id);
        }
        formData.append('attachment', form.attachment);
        
        const response = await api.post(`/leaves/employees/${empId}/requests-with-attachment`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      } else {
        // Use regular request without attachment
        const payload = {
          ...form,
          interim_id: form.interim_id ? Number(form.interim_id) : null,
        };
        
        const response = await requestLeave(empId, payload);
      }
      
      setOpen(false);
      setForm({ employee_id: '', start_date: '', end_date: '', reason: '', type: 'annuel', address_during_leave: '', phone_number: '', interim_id: '', attachment: null });
      await load();
    } catch (error) {
      
      // Handle duplicate error
      if (error.response?.status === 409) {
        alert('Un congé identique a déjà été demandé récemment. Veuillez patienter.');
      } else if (error.response?.status === 400) {
        alert('Erreur de validation: ' + (error.response.data?.message || 'Veuillez vérifier les champs du formulaire'));
      } else if (error.response?.status === 403) {
        alert('Erreur d\'autorisation: ' + (error.response.data?.message || 'Vous n\'êtes pas autorisé à effectuer cette action'));
      } else if (error.response?.status === 500) {
        alert('Erreur serveur: ' + (error.response.data?.message || 'Une erreur est survenue côté serveur'));
      } else {
        alert('Erreur lors de la soumission de la demande: ' + (error.message || 'Erreur inconnue'));
      }
    } finally {
      setLeaveSubmitting(false);
    }
  };

  const onSubmitAbsence = async () => {
    if (absenceSubmitting) {
      return; // Prevent multiple submissions
    }
    
    const empId = (isAdmin || isAdminTenes)
      ? Number(absenceForm.employee_id)
      : me?.employee?.id;
    if (!empId) {
      alert("Veuillez sélectionner un employé");
      return;
    }
    
    // Validate required fields for absence
    if (!absenceForm.start_date || !absenceForm.end_date) {
      alert("Veuillez sélectionner les dates de début et de fin");
      setAbsenceSubmitting(false);
      return;
    }
    
    if (!absenceForm.reason || absenceForm.reason.trim() === '') {
      alert("Veuillez fournir un motif pour l'absence");
      setAbsenceSubmitting(false);
      return;
    }
    
    if (new Date(absenceForm.start_date) > new Date(absenceForm.end_date)) {
      alert("La date de début ne peut pas être après la date de fin");
      setAbsenceSubmitting(false);
      return;
    }
    
    setAbsenceSubmitting(true);
    
    try {
      
      if (absenceForm.attachment) {
        // Use FormData for file upload
        const formData = new FormData();
        formData.append('start_date', absenceForm.start_date);
        formData.append('end_date', absenceForm.end_date);
        formData.append('reason', absenceForm.reason);
        formData.append('type', absenceForm.type);
        formData.append('address_during_leave', absenceForm.address_during_leave);
        formData.append('phone_number', absenceForm.phone_number);
        if (absenceForm.interim_id) {
          formData.append('interim_id', absenceForm.interim_id);
        }
        formData.append('attachment', absenceForm.attachment);
        
        await requestAbsenceWithAttachment(empId, formData);
      } else {
        
        // Use regular request without attachment
        const payload = {
          ...absenceForm,
          interim_id: absenceForm.interim_id ? Number(absenceForm.interim_id) : null,
        };
        
        await requestAbsence(empId, payload);
      }
      
      setAbsenceOpen(false);
      setAbsenceForm({ employee_id: '', start_date: '', end_date: '', reason: '', type: 'maladie', address_during_leave: '', phone_number: '', interim_id: '', attachment: null });
      await loadAbsences();
    } catch (error) {
      
      // Handle duplicate error
      if (error.response?.status === 409) {
        alert('Une absence identique a déjà été demandée récemment. Veuillez patienter.');
      } else {
        alert('Erreur lors de la soumission de la demande d\'absence');
      }
    } finally {
      setAbsenceSubmitting(false);
    }
  };

  const onEditSubmit = async () => {
    if (!editingLeave?.id) return;
    try {
      const { attachment, ...rest } = editForm;
      const payload = {
        ...rest,
        interim_id: rest.interim_id ? Number(rest.interim_id) : null,
      };
      if (editingType === 'absence') {
        await api.put(`/absences/${editingLeave.id}`, payload);
      } else {
        await api.put(`/leaves/${editingLeave.id}`, payload);
      }
      onEditClose();
      if (editingType === 'absence') {
        await loadAbsences();
      } else {
        await load();
      }
    } catch (error) {
      alert('Erreur lors de la mise à jour');
    }
  };

  const openReviewDialog = (id, action, type = 'leave') => {
    setRevLeaveId(id);
    setRevAction(action);
    setRevType(type);
    setRevObsDir('');
    setRevObsResp('');
    setRevOpen(true);
  };

  const closeReviewDialog = () => {
    setRevOpen(false);
    setRevLeaveId(null);
  };

  const submitReview = async () => {
    if (!revLeaveId) return;
    try {
      setRevSubmitting(true);
      
      // Utiliser la bonne fonction selon le type
      if (revType === 'absence') {
        const payload = {
          status: revAction === 'approve' ? 'approved' : 'rejected',
          approved_by: me?.employee?.id
        };
        
        // Only non-admins can set observations
        if (!isAdmin) {
          payload.observation_responsable = revObsResp;
          payload.observation_directeur = revObsDir;
        }
        
        await updateAbsenceStatus(revLeaveId, payload);
      } else {
        const payload = { action: revAction };
        // Only non-admins (i.e., managers/directors in their queues) can set observations here
        if (!isAdmin) {
          payload.observation_responsable = revObsResp;
          payload.observation_directeur = revObsDir;
        }
        await reviewLeave(revLeaveId, payload);
      }
      
      closeReviewDialog();
      if (revType === 'absence') {
        await loadAbsences();
      } else {
        await load();
      }
    } finally {
      setRevSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette demande ?')) {
      return;
    }
    
    try {
      const response = await api.delete(`/leaves/${id}`);
      
      // Show success message
      alert('Demande de congé supprimée avec succès.');
      
      // Reload the leaves list
      await load();
    } catch (error) {
      
      // Handle specific error cases
      if (error.response?.status === 403) {
        alert('Erreur d\'autorisation: ' + (error.response.data?.message || 'Vous n\'êtes pas autorisé à supprimer ce congé.'));
      } else if (error.response?.status === 404) {
        alert('Congé non trouvé: ' + (error.response.data?.message || 'Ce congé n\'existe pas.'));
      } else if (error.response?.status === 500) {
        alert('Erreur serveur: ' + (error.response.data?.message || 'Une erreur est survenue lors de la suppression.'));
      } else {
        alert('Erreur lors de la suppression: ' + (error.message || 'Erreur inconnue'));
      }
    }
  };

  const handleDeleteAbsence = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette absence ?')) return;
    try {
      await deleteAbsence(id);
      await loadAbsences();
    } catch (error) {
      alert('Erreur lors de la suppression');
    }
  };

  const myCols = [
    { field: 'reference_conge', headerName: 'Référence', width: 120 },
    { 
      field: 'start_date', 
      headerName: 'Début', 
      width: 120,
      valueGetter: (value) => {
        if (!value) return '—';
        // Handle DATEONLY format (YYYY-MM-DD string)
        if (typeof value === 'string') {
          const date = new Date(value + 'T00:00:00');
          return isNaN(date.getTime()) ? value : date.toLocaleDateString('fr-FR');
        }
        return value.toLocaleDateString('fr-FR');
      }
    },
    { 
      field: 'end_date', 
      headerName: 'Fin', 
      width: 120,
      valueGetter: (value) => {
        if (!value) return '—';
        // Handle DATEONLY format (YYYY-MM-DD string)
        if (typeof value === 'string') {
          const date = new Date(value + 'T00:00:00');
          return isNaN(date.getTime()) ? value : date.toLocaleDateString('fr-FR');
        }
        return value.toLocaleDateString('fr-FR');
      }
    },
    { 
      field: 'days', 
      headerName: 'Jours', 
      width: 80,
      valueGetter: (params) => {
        if (!params.row) return '—';
        return calculateDays(params.row.start_date, params.row.end_date);
      }
    },
    { 
      field: 'type', 
      headerName: 'Type Congé', 
      width: 160,
      renderCell: (params) => {
        const type = params.row?.type || '';
        return getTypeLabel(type);
      }
    },
    { field: 'reason', headerName: 'Raison', flex: 1 },
        { field: 'status', headerName: 'Statut', width: 120 },
    {
      field: 'actions', headerName: 'Actions', width: 260, sortable: false, filterable: false,
      renderCell: (params) => (
        <>
          <Tooltip title="PDF Demande">
            <IconButton 
              size="small"
              onClick={(e) => {
                  e.stopPropagation(); // Empêche la propagation du clic
                  setPdfType('request');
                  setPdfData({ employee: me?.employee, leave: params.row });
                  setPdfOpen(true);
                }}
              sx={{ color: 'blue' }}
            >
              <PictureAsPdfIcon />
            </IconButton>
          </Tooltip>
          <span style={{ marginLeft: 8 }}>
            <LeavePDFViewer leaveId={params.row.id} size="small" variant="outlined" color="secondary" />
          </span>
          {params.row.status === 'approved' && (
            <Tooltip title="PDF Acceptée">
              <IconButton 
                size="small"
                sx={{ ml: 1, color: 'red' }}
                onClick={(e) => {
                  e.stopPropagation(); // Empêche la propagation du clic
                  setPdfType('accepted');
                  setPdfData({ employee: me?.employee, leave: params.row });
                  setPdfOpen(true);
                }}
              >
                <PictureAsPdfIcon />
              </IconButton>
            </Tooltip>
          )}
          {params.row.status !== 'approved' && params.row.status !== 'rejected' && (
            <Tooltip title="Supprimer">
              <IconButton size="small" sx={{ ml: 1 }} onClick={(e) => {
                e.stopPropagation(); // Empêche la propagation du clic
                handleDelete(params.row.id);
              }}>
                <DeleteIcon color="error" />
              </IconButton>
            </Tooltip>
          )}
        </>
      )
    }
  ];

  const pendingCols = [
    { field: 'reference_conge', headerName: 'Référence', width: 120 },
    { field: 'start_date', headerName: 'Début', width: 120 },
    { field: 'end_date', headerName: 'Fin', width: 120 },
    { 
      field: 'days', 
      headerName: 'Jours', 
      width: 80,
      valueGetter: (params) => {
        if (!params.row) return '—';
        return calculateDays(params.row.start_date, params.row.end_date);
      }
    },
    { 
      field: 'type', 
      headerName: 'Type Congé', 
      width: 160,
      renderCell: (params) => {
        const type = params.row?.type || '';
        return getTypeLabel(type);
      }
    },
    { field: 'reason', headerName: 'Raison', flex: 1 },
    {
      field: 'actions', headerName: 'Actions', width: 220, renderCell: (params) => (
        <>
          {canSeeAll && params.row.status !== 'approved' && params.row.status !== 'rejected' && (
            <>
              <Button size="small" color="success" onClick={(e) => {
                e.stopPropagation(); // Empêche la propagation du clic
                openReviewDialog(params.row.id, 'approve');
              }}>Approuver</Button>
              <Button size="small" color="error" sx={{ ml: 1 }} onClick={(e) => {
                e.stopPropagation(); // Empêche la propagation du clic
                openReviewDialog(params.row.id, 'reject');
              }}>Refuser</Button>
            </>
          )}
          {canSeeAll && params.row.status !== 'approved' && params.row.status !== 'rejected' && (isAdmin || isAdminTenes) && (
            <Tooltip title="Supprimer">
              <IconButton size="small" sx={{ ml: 1 }} onClick={(e) => {
                e.stopPropagation(); // Empêche la propagation du clic
                handleDelete(params.row.id);
              }}>
                <DeleteIcon color="error" />
              </IconButton>
            </Tooltip>
          )}
        </>
      )
    }
  ];

  // Review queue columns for managers/site directors
  const reviewCols = [
    { field: 'reference_conge', headerName: 'Référence', width: 120 },
    { field: 'start_date', headerName: 'Début', width: 120 },
    { field: 'end_date', headerName: 'Fin', width: 120 },
    { 
      field: 'days', 
      headerName: 'Jours', 
      width: 80,
      valueGetter: (params) => {
        if (!params.row) return '—';
        return calculateDays(params.row.start_date, params.row.end_date);
      }
    },
    { 
      field: 'type', 
      headerName: 'Type Congé', 
      width: 160,
      renderCell: (params) => {
        const type = params.row?.type || '';
        return getTypeLabel(type);
      }
    },
    { field: 'reason', headerName: 'Raison', flex: 1 },
    {
      field: 'who', headerName: 'Rôle', width: 130,
      valueGetter: (p) => (p?.row?._obsRole === 'responsable' ? 'Responsable' : p?.row?._obsRole === 'directeur' ? 'Directeur' : '—')
    },
    {
      field: 'observation', headerName: 'Observation à saisir', flex: 1,
      valueGetter: (p) => {
        const r = p?.row || {};
        if (r._obsRole === 'responsable') return r.observation_responsable || '';
        if (r._obsRole === 'directeur') return r.observation_directeur || '';
        return '';
      }
    },
    {
      field: 'actions', headerName: 'Action', width: 180, renderCell: (params) => (
        <Button size="small" variant="contained" onClick={() => {
          setObsLeaveId(params.row.id);
          setObsRole(params.row._obsRole);
          setObsText('');
          setObsOpen(true);
        }}>Ajouter observation</Button>
      )
    }
  ];

  // Admin/comptable: all leaves listing with PDFs
  const allCols = [
    { field: 'reference_conge', headerName: 'Référence', width: 120, headerClassName: 'super-app-theme--header' },
    { field: 'employeeName', headerName: 'Employé', width: 180, headerClassName: 'super-app-theme--header' },
    { 
      field: 'start_date', 
      headerName: 'Début', 
      width: 120, 
      headerClassName: 'super-app-theme--header',
      valueGetter: (value) => {
        if (!value) return '—';
        // Handle DATEONLY format (YYYY-MM-DD string)
        if (typeof value === 'string') {
          const date = new Date(value + 'T00:00:00');
          return isNaN(date.getTime()) ? value : date.toLocaleDateString('fr-FR');
        }
        return value.toLocaleDateString('fr-FR');
      }
    },
    { 
      field: 'end_date', 
      headerName: 'Fin', 
      width: 120, 
      headerClassName: 'super-app-theme--header',
      valueGetter: (value) => {
        if (!value) return '—';
        // Handle DATEONLY format (YYYY-MM-DD string)
        if (typeof value === 'string') {
          const date = new Date(value + 'T00:00:00');
          return isNaN(date.getTime()) ? value : date.toLocaleDateString('fr-FR');
        }
        return value.toLocaleDateString('fr-FR');
      }
    },
    { field: 'days', headerName: 'Jours', width: 80, headerClassName: 'super-app-theme--header',
      valueGetter: (params) => {
        if (!params.row) return '—';
        return calculateDays(params.row.start_date, params.row.end_date);
      }
    },
    { 
      field: 'type', 
      headerName: 'Type congé', 
      width: 160, 
      headerClassName: 'super-app-theme--header',
      renderCell: (params) => {
        const type = params.row?.type || '';
        return getTypeLabel(type);
      }
    },
    { field: 'reason', headerName: 'Raison', flex: 1, headerClassName: 'super-app-theme--header' },
    { 
      field: 'attachment', 
      headerName: 'Pièce jointe', 
      width: 120, 
      headerClassName: 'super-app-theme--header',
      renderCell: (params) => {
        const attachment = params.row?.attachment;
        if (!attachment) return <span>-</span>;
        
        return (
          <Tooltip title="Voir la pièce jointe">
            <IconButton 
              size="small"
              onClick={() => {
                const fileUrl = `${baseURL}/leaves/uploads/leaves/${attachment}`;
                window.open(fileUrl, '_blank');
              }}
            >
              <AttachFileIcon />
            </IconButton>
          </Tooltip>
        );
      }
    },
    { field: 'status', headerName: 'Statut', width: 120, headerClassName: 'super-app-theme--header', renderCell: (params) => getStatusChip(params.row) },
        {
      field: 'actions', headerName: 'Actions', width: 320, sortable: false, filterable: false, headerClassName: 'super-app-theme--header',
      renderCell: (params) => {
        const row = params?.row || {};
        const emp = row.employee || row.Employee;
        return (
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', py: 1 }}>
            <Tooltip title="PDF Demande">
              <IconButton 
                size="small"
                onClick={() => {
                  setPdfType('request');
                  setPdfData({ employee: emp, leave: row });
                  setPdfOpen(true);
                }}
                sx={{ color: 'blue' }}
              >
                <PictureAsPdfIcon />
              </IconButton>
            </Tooltip>
            <LeavePDFViewer leaveId={row.id} size="small" variant="outlined" color="secondary" />
            {row.status === 'approved' && (
              <Tooltip title="PDF Acceptée">
                <IconButton 
                  size="small"
                  sx={{ color: 'red' }}
                  onClick={() => {
                    setPdfType('accepted');
                    setPdfData({ employee: emp, leave: row });
                    setPdfOpen(true);
                  }}
                >
                  <PictureAsPdfIcon />
                </IconButton>
              </Tooltip>
            )}
            {canSeeAll && row.status !== 'approved' && row.status !== 'rejected' && (
              <>
                <Button size="small" color="success" variant="contained" onClick={(e) => {
                  e.stopPropagation(); // Empêche la propagation du clic
                  openReviewDialog(row.id, 'approve');
                }}>✓</Button>
                <Button size="small" color="error" variant="contained" onClick={(e) => {
                  e.stopPropagation(); // Empêche la propagation du clic
                  openReviewDialog(row.id, 'reject');
                }}>✗</Button>
              </>
            )}
            {canSeeAll && (isAdmin || isAdminTenes) && (
              <Tooltip title="Supprimer">
                <IconButton size="small" onClick={(e) => {
                  e.stopPropagation(); // Empêche la propagation du clic
                  handleDelete(row.id);
                }}>
                  <DeleteIcon color="error" />
                </IconButton>
              </Tooltip>
            )}
            {canSeeAll && (isAdmin || isAdminTenes) && (
              <Tooltip title="Modifier">
                <IconButton 
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation(); // Empêche la propagation du clic
                    onEditOpen(row, 'leave');
                  }}
                  sx={{ color: 'blue' }}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        )
      }
    }
  ];

  // Colonnes spécifiques pour les absences
  const absenceCols = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'employeeName', headerName: 'Employé', width: 180 },
    {
      field: 'type',
      headerName: 'Type',
      width: 160,
      renderCell: (params) => {
        const type = params.row?.type || '';
        return getTypeLabel(type);
      }
    },
    { field: 'reason', headerName: 'Raison', flex: 1 },
    {
      field: 'start_date',
      headerName: 'Date début',
      width: 120,
      valueGetter: (value) => {
        if (!value) return '—';
        // Handle DATEONLY format (YYYY-MM-DD string)
        if (typeof value === 'string') {
          const date = new Date(value + 'T00:00:00');
          return isNaN(date.getTime()) ? value : date.toLocaleDateString('fr-FR');
        }
        return value.toLocaleDateString('fr-FR');
      }
    },
    {
      field: 'end_date',
      headerName: 'Date fin',
      width: 120,
      valueGetter: (value) => {
        if (!value) return '—';
        // Handle DATEONLY format (YYYY-MM-DD string)
        if (typeof value === 'string') {
          const date = new Date(value + 'T00:00:00');
          return isNaN(date.getTime()) ? value : date.toLocaleDateString('fr-FR');
        }
        return value.toLocaleDateString('fr-FR');
      }
    },
    { 
      field: 'days', 
      headerName: 'Jours', 
      width: 80,
      valueGetter: (params) => {
        if (!params.row) return '—';
        return calculateDays(params.row.start_date, params.row.end_date);
      }
    },
    {
      field: 'status',
      headerName: 'Statut',
      width: 120,
      renderCell: (params) => getStatusChip(params.row)
    },
    {
      field: 'attachment',
      headerName: 'Pièce jointe',
      width: 120,
      renderCell: (params) => {
        const attachment = params.row?.attachment;
        if (!attachment) return <span>-</span>;
        
        return (
          <Tooltip title="Voir la pièce jointe">
            <IconButton 
              size="small"
              onClick={() => window.open(`${baseURL}/uploads/absences/${attachment}`, '_blank')}
            >
              <AttachFileIcon />
            </IconButton>
          </Tooltip>
        );
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 240,
      renderCell: (params) => {
        const row = params?.row || {};
        return (
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', py: 1 }}>
            {/* PDF Acceptée pour les absences approuvées - Autorisation d'Absence */}
            {row.status === 'approved' && (
              <Tooltip title="PDF Acceptée - Autorisation d'Absence">
                <IconButton 
                  size="small"
                  sx={{ color: 'red' }}
                  onClick={(e) => {
                    e.stopPropagation(); // Empêche la propagation du clic
                    setPdfType('accepted');
                    setPdfData({ employee: row.employee, leave: row });
                    setPdfOpen(true);
                  }}
                >
                  <PictureAsPdfIcon />
                </IconButton>
              </Tooltip>
            )}
            
            {canSeeAll && row.status !== 'approved' && row.status !== 'rejected' && (
              <>
                <Button size="small" color="success" variant="contained" onClick={(e) => {
                  e.stopPropagation(); // Empêche la propagation du clic
                  openReviewDialog(row.id, 'approve', 'absence');
                }}>✓</Button>
                <Button size="small" color="error" variant="contained" onClick={(e) => {
                  e.stopPropagation(); // Empêche la propagation du clic
                  openReviewDialog(row.id, 'reject', 'absence');
                }}>✗</Button>
              </>
            )}
            {canSeeAll && (isAdmin || isAdminTenes) && (
              <Tooltip title="Supprimer">
                <IconButton size="small" onClick={(e) => {
                  e.stopPropagation(); // Empêche la propagation du clic
                  handleDeleteAbsence(row.id);
                }}>
                  <DeleteIcon color="error" />
                </IconButton>
              </Tooltip>
            )}
            {canSeeAll && (isAdmin || isAdminTenes) && (
              <Tooltip title="Modifier">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation(); // Empêche la propagation du clic
                    onEditOpen(row, 'absence');
                  }}
                  sx={{ color: 'blue' }}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        );
      }
    }
  ];

  return (
    <div>
      <h2>Absences/Congés</h2>
      
      {/* Onglets */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={currentTab} onChange={handleTabChange}>
          <Tab label="Liste des congés" />
          <Tab label="Liste des absences" />
        </Tabs>
      </Box>

      {/* Contenu de l'onglet Congés */}
      {currentTab === 0 && (
        <div>
          <Stack spacing={2} direction="row" sx={{ mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Alert severity="info">Solde de congés: {me?.employee?.leave_balance ?? '—'}</Alert>
            <Button variant="contained" onClick={onOpen}>Demander un congé</Button>
            
            {/* Filters */}
            <TextField
              size="small"
              label="Rechercher employé"
              value={filterEmployee}
              onChange={(e) => setFilterEmployee(e.target.value)}
              sx={{ minWidth: 200 }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Statut</InputLabel>
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                label="Statut"
              >
                {filterOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Type Congé</InputLabel>
              <Select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                label="Type Congé"
              >
                <MenuItem value="all">Tous les types</MenuItem>
                <MenuItem value="annuel">Annuel</MenuItem>
                <MenuItem value="sans_solde">Sans Solde</MenuItem>
                <MenuItem value="autre">Autre</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          <div style={{ height: 800, marginTop: 10 }}>
            <DataGrid 
              rows={filteredRows} 
              columns={allCols} 
              getRowId={(r) => r.id} 
              pageSize={20} 
              getRowHeight={() => 80}
              onRowClick={(params) => handleRowClick(params, 'leave')}
              sx={{
                '& .super-app-theme--header': {
                  backgroundColor: '#1976d2',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 600,
                },
                '& .MuiDataGrid-root': {
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: '#1976d2',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'white',
                  borderBottom: '2px solid #1565c0',
                },
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: '#1976d2',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 600,
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 600,
                },
                '& .MuiDataGrid-row': {
                  '&:nth-of-type(even)': {
                    backgroundColor: '#fafafa',
                  },
                  '&:hover': {
                    backgroundColor: '#e3f2fd',
                    cursor: 'pointer'
                  },
                  '&.Mui-selected': {
                    backgroundColor: '#e3f2fd',
                  },
                },
              }}
            />
          </div>
        </div>
      )}

      {/* Contenu de l'onglet Absences */}
      {currentTab === 1 && (
        <div>
          <Stack spacing={2} direction="row" sx={{ mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button variant="contained" color="secondary" onClick={onAbsenceOpen}>Demander une absence</Button>
            
            {/* Filters pour absences - même style que les congés */}
            <TextField
              size="small"
              label="Rechercher employé"
              value={filterEmployee}
              onChange={(e) => setFilterEmployee(e.target.value)}
              sx={{ minWidth: 200 }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Statut</InputLabel>
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                label="Statut"
              >
                {filterOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Type Absence</InputLabel>
              <Select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                label="Type Absence"
              >
                <MenuItem value="all">Tous les types</MenuItem>
                <MenuItem value="maladie">Maladie</MenuItem>
                <MenuItem value="maternite">Maternité</MenuItem>
                <MenuItem value="recuperation">Récupération</MenuItem>
                <MenuItem value="accident_travail">Accident de travail</MenuItem>
                <MenuItem value="mariage">Evenement familial : Mariage</MenuItem>
                <MenuItem value="deces_famille">Evenement familial : Décès</MenuItem>
                <MenuItem value="formation">Formation</MenuItem>
                <MenuItem value="evenement_familial_naissance">Evenement familial : Naissance</MenuItem>
                <MenuItem value="evenement_familial_circoncision">Evenement familial : Circoncision (ختان)</MenuItem>
                <MenuItem value="autre">Autre</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          <div style={{ height: 800, marginTop: 10 }}>
            <DataGrid 
              rows={filteredAbsenceRows} 
              columns={absenceCols} 
              getRowId={(r) => r.id} 
              pageSize={20} 
              getRowHeight={() => 80}
              onRowClick={(params) => handleRowClick(params, 'absence')}
              sx={{
                '& .super-app-theme--header': {
                  backgroundColor: '#1976d2',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 600,
                },
                '& .MuiDataGrid-root': {
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: '#1976d2',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'white',
                  borderBottom: '2px solid #1565c0',
                },
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: '#1976d2',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 600,
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 600,
                },
                '& .MuiDataGrid-row': {
                  '&:nth-of-type(even)': {
                    backgroundColor: '#fafafa',
                  },
                  '&:hover': {
                    backgroundColor: '#e3f2fd',
                    cursor: 'pointer'
                  },
                  '&.Mui-selected': {
                    backgroundColor: '#e3f2fd',
                  },
                },
              }}
            />
          </div>
        </div>
      )}

      {/* Dialog pour demande de congé */}
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Demander un congé</DialogTitle>
        <DialogContent>
          {(isAdmin || isAdminTenes) ? (
            <FormControl fullWidth margin="dense">
              <InputLabel>Employé</InputLabel>
              <Select label="Employé" name="employee_id" value={form.employee_id} onChange={onChange}>
                <MenuItem value="">Sélectionner</MenuItem>
                {(allEmployees || []).map((e) => (
                  <MenuItem key={e.id} value={e.id}>
                    {`${e.first_name || ''} ${e.last_name || ''}`.trim()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <TextField
              fullWidth
              margin="dense"
              label="Employé"
              value={`${me?.employee?.first_name || ''} ${me?.employee?.last_name || ''}`.trim()}
              disabled
            />
          )}
          <FormControl fullWidth margin="dense">
            <InputLabel>Type de congé</InputLabel>
            <Select label="Type de congé" name="type" value={form.type} onChange={onChange}>
              <MenuItem value="annuel">Annuel</MenuItem>
              <MenuItem value="sans_solde">Sans Solde</MenuItem>
              <MenuItem value="autre">Autre</MenuItem>
            </Select>
          </FormControl>
          <TextField fullWidth margin="dense" type="date" label="Date de début" name="start_date" value={form.start_date} onChange={onChange} InputLabelProps={{ shrink: true }} />
          <TextField fullWidth margin="dense" type="date" label="Date de fin" name="end_date" value={form.end_date} onChange={onChange} InputLabelProps={{ shrink: true }} />
          <TextField fullWidth margin="dense" label="Motif" name="reason" value={form.reason} onChange={onChange} multiline minRows={3} />
          <TextField fullWidth margin="dense" label="Adresse durant le congé" name="address_during_leave" value={form.address_during_leave} onChange={onChange} />
          <TextField fullWidth margin="dense" label="N° de téléphone (pendant le congé)" name="phone_number" value={form.phone_number} onChange={onChange} />
          <FormControl fullWidth margin="dense">
            <InputLabel>Intérim</InputLabel>
            <Select label="Intérim" name="interim_id" value={form.interim_id} onChange={onChange}>
              <MenuItem value="">Aucun</MenuItem>
              {(interimEmployees || [])
                .sort((a, b) => {
                  const nameA = `${a.first_name || ''} ${a.last_name || ''}`.trim().toLowerCase();
                  const nameB = `${b.first_name || ''} ${b.last_name || ''}`.trim().toLowerCase();
                  return nameA.localeCompare(nameB);
                })
                .map((e) => (
                <MenuItem key={e.id} value={e.id}>
                  {`${e.first_name || ''} ${e.last_name || ''}`.trim()}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={leaveSubmitting}>Annuler</Button>
          <Button onClick={onSubmit} variant="contained" disabled={leaveSubmitting}>
            {leaveSubmitting ? 'Envoi en cours...' : 'Envoyer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog pour demande d'absence */}
      <Dialog open={absenceOpen} onClose={onAbsenceClose} maxWidth="sm" fullWidth>
        <DialogTitle>Demander une absence</DialogTitle>
        <DialogContent>
          {(isAdmin || isAdminTenes) ? (
            <FormControl fullWidth margin="dense">
              <InputLabel>Employé</InputLabel>
              <Select label="Employé" name="employee_id" value={absenceForm.employee_id} onChange={onAbsenceChange}>
                <MenuItem value="">Sélectionner</MenuItem>
                {(allEmployees || []).map((e) => (
                  <MenuItem key={e.id} value={e.id}>
                    {`${e.first_name || ''} ${e.last_name || ''}`.trim()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <TextField
              fullWidth
              margin="dense"
              label="Employé"
              value={`${me?.employee?.first_name || ''} ${me?.employee?.last_name || ''}`.trim()}
              disabled
            />
          )}
          <FormControl fullWidth margin="dense">
            <InputLabel>Type d'absence</InputLabel>
            <Select label="Type d'absence" name="type" value={absenceForm.type} onChange={onAbsenceChange}>
              <MenuItem value="maladie">Maladie</MenuItem>
              <MenuItem value="maternite">Maternité</MenuItem>
              <MenuItem value="recuperation">Récupération</MenuItem>
              <MenuItem value="accident_travail">Accident de travail</MenuItem>
              <MenuItem value="mariage">Evenement familial : Mariage</MenuItem>
              <MenuItem value="deces_famille">Evenement familial : Décès</MenuItem>
              <MenuItem value="formation">Formation</MenuItem>
              <MenuItem value="evenement_familial_naissance">Evenement familial : Naissance</MenuItem>
              <MenuItem value="evenement_familial_circoncision">Evenement familial : Circoncision (ختان)</MenuItem>
              <MenuItem value="autre">Autre</MenuItem>
            </Select>
          </FormControl>
          <TextField fullWidth margin="dense" type="date" label="Date de début" name="start_date" value={absenceForm.start_date} onChange={onAbsenceChange} InputLabelProps={{ shrink: true }} />
          <TextField fullWidth margin="dense" type="date" label="Date de fin" name="end_date" value={absenceForm.end_date} onChange={onAbsenceChange} InputLabelProps={{ shrink: true }} />
          <TextField fullWidth margin="dense" label="Motif" name="reason" value={absenceForm.reason} onChange={onAbsenceChange} multiline minRows={3} />
          <TextField fullWidth margin="dense" label="Adresse durant l'absence" name="address_during_leave" value={absenceForm.address_during_leave} onChange={onAbsenceChange} />
          <TextField fullWidth margin="dense" label="N° de téléphone (pendant l'absence)" name="phone_number" value={absenceForm.phone_number} onChange={onAbsenceChange} />
          <FormControl fullWidth margin="dense">
            <InputLabel>Intérim</InputLabel>
            <Select label="Intérim" name="interim_id" value={absenceForm.interim_id} onChange={onAbsenceChange}>
              <MenuItem value="">Aucun</MenuItem>
              {(interimEmployees || [])
                .sort((a, b) => {
                  const nameA = `${a.first_name || ''} ${a.last_name || ''}`.trim().toLowerCase();
                  const nameB = `${b.first_name || ''} ${b.last_name || ''}`.trim().toLowerCase();
                  return nameA.localeCompare(nameB);
                })
                .map((e) => (
                <MenuItem key={e.id} value={e.id}>
                  {`${e.first_name || ''} ${e.last_name || ''}`.trim()}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            margin="dense"
            type="file"
            label="Pièce jointe"
            name="attachment"
            onChange={onAbsenceChange}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              accept: '.pdf,.jpg,.jpeg,.png,.gif'
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onAbsenceClose} disabled={absenceSubmitting}>Annuler</Button>
          <Button onClick={onSubmitAbsence} variant="contained" disabled={absenceSubmitting}>
            {absenceSubmitting ? 'Envoi en cours...' : 'Envoyer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog pour modification */}
      <Dialog open={editOpen} onClose={onEditClose} maxWidth="sm" fullWidth>
        <DialogTitle>Modifier le congé</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="dense">
            <InputLabel>Type de congé</InputLabel>
            <Select label="Type d'congé" name="type" value={editForm.type} onChange={onEditChange}>
              <MenuItem value="annuel">Annuel</MenuItem>
              <MenuItem value="maladie">Maladie</MenuItem>
              <MenuItem value="maternite">Maternité</MenuItem>
              <MenuItem value="recuperation">Récupération</MenuItem>
              <MenuItem value="sans_solde">Sans solde</MenuItem>
              <MenuItem value="exceptionnel">Exceptionnel</MenuItem>
              <MenuItem value="autre">Autre</MenuItem>
              <MenuItem value="deces">Evenement familial : Décès</MenuItem>
              <MenuItem value="mariage">Evenement familial : Mariage</MenuItem>
              <MenuItem value="naissance">Evenement familial : Naissance</MenuItem>
              <MenuItem value="demenagement">Evenement familial : Déménagement</MenuItem>
              <MenuItem value="circoncision">Evenement familial : circoncision ختان</MenuItem>
            </Select>
          </FormControl>
          <TextField fullWidth margin="dense" type="date" label="Date de début" name="start_date" value={editForm.start_date} onChange={onEditChange} InputLabelProps={{ shrink: true }} />
          <TextField fullWidth margin="dense" type="date" label="Date de fin" name="end_date" value={editForm.end_date} onChange={onEditChange} InputLabelProps={{ shrink: true }} />
          <TextField fullWidth margin="dense" label="Motif" name="reason" value={editForm.reason} onChange={onEditChange} multiline minRows={3} />
          <TextField fullWidth margin="dense" label="Adresse durant le congé" name="address_during_leave" value={editForm.address_during_leave} onChange={onEditChange} />
          <TextField fullWidth margin="dense" label="N° de téléphone (pendant congé)" name="phone_number" value={editForm.phone_number} onChange={onEditChange} />
          <FormControl fullWidth margin="dense">
            <InputLabel>Intérim</InputLabel>
            <Select label="Intérim" name="interim_id" value={editForm.interim_id} onChange={onEditChange}>
              <MenuItem value="">Aucun</MenuItem>
              {(interimEmployees || [])
                .sort((a, b) => {
                  const nameA = `${a.first_name || ''} ${a.last_name || ''}`.trim().toLowerCase();
                  const nameB = `${b.first_name || ''} ${b.last_name || ''}`.trim().toLowerCase();
                  return nameA.localeCompare(nameB);
                })
                .map((e) => (
                <MenuItem key={e.id} value={e.id}>
                  {`${e.first_name || ''} ${e.last_name || ''}`.trim()}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={onEditClose}>Annuler</Button>
          <Button onClick={onEditSubmit} variant="contained">Enregistrer</Button>
        </DialogActions>
      </Dialog>

      {/* Observation-only dialog */}
      <Dialog open={obsOpen} onClose={() => setObsOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Ajouter une observation ({obsRole === 'responsable' ? 'Responsable' : obsRole === 'directeur' ? 'Directeur' : ''})</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="dense"
            label="Observation"
            multiline
            minRows={3}
            value={obsText}
            onChange={(e) => setObsText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setObsOpen(false)}>Annuler</Button>
          <Button
            onClick={async () => {
              if (!obsLeaveId) return;
              try {
                setObsSubmitting(true);
                await observeLeave(obsLeaveId, obsText);
                setObsOpen(false);
                setObsLeaveId(null);
                setObsText('');
                await load();
              } finally {
                setObsSubmitting(false);
              }
            }}
            variant="contained"
            disabled={obsSubmitting || !obsText.trim()}
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>
      {pdfOpen && (
        <PDFModalLeave
          open={pdfOpen}
          onClose={() => setPdfOpen(false)}
          type={pdfType}
          data={pdfData}
        />
      )}

      {/* Review with observations dialog */}
      <Dialog open={revOpen} onClose={closeReviewDialog} fullWidth maxWidth="sm">
        <DialogTitle>{revAction === 'approve' ? 'Approuver la demande' : 'Refuser la demande'}</DialogTitle>
        <DialogContent>
          {/* Admins should not modify others' observations in pending list */}
          {!isAdmin && (
            <>
              <TextField
                fullWidth
                margin="dense"
                label="Observation (Responsable)"
                multiline
                minRows={2}
                value={revObsResp}
                onChange={(e) => setRevObsResp(e.target.value)}
              />
              <TextField
                fullWidth
                margin="dense"
                label="Observation (Directeur)"
                multiline
                minRows={2}
                value={revObsDir}
                onChange={(e) => setRevObsDir(e.target.value)}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeReviewDialog}>Annuler</Button>
          <Button onClick={submitReview} variant="contained" disabled={revSubmitting}>
            {revAction === 'approve' ? 'Approuver' : 'Refuser'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog pour modification */}
      <Dialog open={editOpen} onClose={onEditClose} maxWidth="sm" fullWidth>
        <DialogTitle>Modifier le congé</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="dense">
            <InputLabel>Type de congé</InputLabel>
            <Select label="Type de congé" name="type" value={editForm.type} onChange={onEditChange}>
              <MenuItem value="annuel">Annuel</MenuItem>
              <MenuItem value="maladie">Maladie</MenuItem>
              <MenuItem value="maternite">Maternité</MenuItem>
              <MenuItem value="recuperation">Récupération</MenuItem>
              <MenuItem value="sans_solde">Sans solde</MenuItem>
              <MenuItem value="exceptionnel">Exceptionnel</MenuItem>
              <MenuItem value="autre">Autre</MenuItem>
              <MenuItem value="deces">Evenement familial : Décès</MenuItem>
              <MenuItem value="mariage">Evenement familial : Mariage</MenuItem>
              <MenuItem value="naissance">Evenement familial : Naissance</MenuItem>
              <MenuItem value="demenagement">Evenement familial : Déménagement</MenuItem>
              <MenuItem value="circoncision">Evenement familial : circoncision ختان</MenuItem>
            </Select>
          </FormControl>
          <TextField fullWidth margin="dense" type="date" label="Date de début" name="start_date" value={editForm.start_date} onChange={onEditChange} InputLabelProps={{ shrink: true }} />
          <TextField fullWidth margin="dense" type="date" label="Date de fin" name="end_date" value={editForm.end_date} onChange={onEditChange} InputLabelProps={{ shrink: true }} />
          <TextField fullWidth margin="dense" label="Motif" name="reason" value={editForm.reason} onChange={onEditChange} multiline minRows={3} />
          <TextField fullWidth margin="dense" label="Adresse durant le congé" name="address_during_leave" value={editForm.address_during_leave} onChange={onEditChange} />
          <TextField fullWidth margin="dense" label="N° de téléphone (pendant le congé)" name="phone_number" value={editForm.phone_number} onChange={onEditChange} />
          <FormControl fullWidth margin="dense">
            <InputLabel>Intérim</InputLabel>
            <Select label="Intérim" name="interim_id" value={editForm.interim_id} onChange={onEditChange}>
              <MenuItem value="">Aucun</MenuItem>
              {(interimEmployees || [])
                .sort((a, b) => {
                  const nameA = `${a.first_name || ''} ${a.last_name || ''}`.trim().toLowerCase();
                  const nameB = `${b.first_name || ''} ${b.last_name || ''}`.trim().toLowerCase();
                  return nameA.localeCompare(nameB);
                })
                .map((e) => (
                <MenuItem key={e.id} value={e.id}>
                  {`${e.first_name || ''} ${e.last_name || ''}`.trim()}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={onEditClose}>Annuler</Button>
          <Button onClick={onEditSubmit} variant="contained">Enregistrer</Button>
        </DialogActions>
      </Dialog>

      {/* PDF Modal */}
      <PDFModalLeave open={pdfOpen} onClose={() => setPdfOpen(false)} type={pdfType} data={pdfData} />

      {/* Details Modal */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Détails {detailsType === 'absence' ? 'de l\'absence' : 'du congé'}</DialogTitle>
        <DialogContent>
          {selectedDetails && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '10px' }}>
              <div><strong>Employé:</strong> {selectedDetails.employee ? `${selectedDetails.employee.first_name || ''} ${selectedDetails.employee.last_name || ''}`.trim() : selectedDetails.employeeName || '—'}</div>
              <div><strong>Type:</strong> {selectedDetails.type ? getTypeLabel(selectedDetails.type) : '—'}</div>
              <div><strong>Date de début:</strong> {selectedDetails.start_date ? new Date(selectedDetails.start_date + 'T00:00:00').toLocaleDateString('fr-FR') : '—'}</div>
              <div><strong>Date de fin:</strong> {selectedDetails.end_date ? new Date(selectedDetails.end_date + 'T00:00:00').toLocaleDateString('fr-FR') : '—'}</div>
              <div><strong>Jours:</strong> {calculateDays(selectedDetails.start_date, selectedDetails.end_date)}</div>
              <div><strong>Motif:</strong> {selectedDetails.reason || '—'}</div>
              {selectedDetails.address_during_leave && (
                <div><strong>Adresse pendant le {detailsType === 'absence' ? 'congé' : 'congé'}:</strong> {selectedDetails.address_during_leave}</div>
              )}
              {selectedDetails.phone_number && (
                <div><strong>Téléphone:</strong> {selectedDetails.phone_number}</div>
              )}
              {selectedDetails.interim_id && (
                <div><strong>Intérim:</strong> {selectedDetails.interim ? `${selectedDetails.interim.first_name || ''} ${selectedDetails.interim.last_name || ''}`.trim() : selectedDetails.interim_name || '—'}</div>
              )}
              <div><strong>Statut:</strong> 
                {selectedDetails.status === 'approved' || selectedDetails.approved ? 
                  <span style={{ color: 'green', marginLeft: '8px' }}>✓ Validé</span> : 
                  selectedDetails.status === 'rejected' || selectedDetails.refused ? 
                  <span style={{ color: 'red', marginLeft: '8px' }}>✗ Refusé</span> :
                  <span style={{ color: 'orange', marginLeft: '8px' }}>⏳ En attente</span>
                }
              </div>
              {selectedDetails.observation_responsable && (
                <div><strong>Observation responsable:</strong> {selectedDetails.observation_responsable}</div>
              )}
              {selectedDetails.observation_directeur && (
                <div><strong>Observation directeur:</strong> {selectedDetails.observation_directeur}</div>
              )}
              <div><strong>Créé le:</strong> {selectedDetails.createdAt ? new Date(selectedDetails.createdAt).toLocaleDateString('fr-FR') + ' à ' + new Date(selectedDetails.createdAt).toLocaleTimeString('fr-FR') : '—'}</div>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default LeavesPage;
